import { invokeFunction, supabase } from "./supabase";
import { isFixtureMode } from "./memberService";
import type { Opportunity } from "./types";
import type { CrmOutputCandidate } from "./workflowTypes";

export const isAdminFixtureMode = isFixtureMode;

async function fixtures() {
  if (!import.meta.env.DEV) throw new Error("Development fixtures are unavailable in production.");
  return import("./fixtures");
}

export interface AdminApplication {
  id: string;
  name: string;
  email: string;
  discordUsername: string;
  country: string;
  status: string;
  submittedAt: string;
  dateOfBirth: string;
  guardianApproved: boolean;
  whyEntry: string;
  qualificationSignals: string;
  callPlan: string;
  primaryRoute: string;
  backupRoute: string;
  availabilityBand: string;
  portfolioUrl: string;
  tools: string;
  sampleVersion: string;
  sampleResponse: string;
}

export interface ReviewScores {
  routeFit: number;
  judgment: number;
  communication: number;
  execution: number;
  integrity: number;
}
export type ApplicationDecision = "invited" | "waitlisted" | "declined";

export interface ServiceHealth {
  service: string;
  status: "ok" | "degraded" | "down" | "not_configured";
  detail: string;
}

export async function listApplications(): Promise<AdminApplication[]> {
  if (isAdminFixtureMode) return (await fixtures()).readFixtureState().applications;
  const { data, error } = await supabase!.from("applications")
    .select("id,name,email,discord_username,country,status,submitted_at,date_of_birth,why_entry,qualification_signals,call_plan,primary_route,backup_route,availability_band,portfolio_url,tools,sample_version,sample_response,guardian_consents(approved_at)")
    .order("submitted_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map((row) => ({
    id: row.id, name: row.name, email: row.email, discordUsername: row.discord_username,
    country: row.country, status: row.status, submittedAt: row.submitted_at,
    dateOfBirth: row.date_of_birth, guardianApproved: Boolean(row.guardian_consents?.[0]?.approved_at),
    whyEntry: row.why_entry, qualificationSignals: row.qualification_signals, callPlan: row.call_plan,
    primaryRoute: row.primary_route, backupRoute: row.backup_route, availabilityBand: row.availability_band,
    portfolioUrl: row.portfolio_url, tools: row.tools, sampleVersion: row.sample_version, sampleResponse: row.sample_response,
  }));
}

export async function reviewApplication(id: string, decision: ApplicationDecision, scores: ReviewScores, notes: string) {
  if (isAdminFixtureMode) {
    const api = await fixtures();
    const state = api.readFixtureState();
    const application = state.applications.find((item) => item.id === id);
    if (!application) throw new Error("Application not found.");
    application.status = decision;
    state.audit.unshift({ id: crypto.randomUUID(), eventType: `application.${decision}`, entity: application.name, createdAt: new Date().toISOString() });
    api.writeFixtureState(state);
    return;
  }
  await invokeFunction("review-application", { applicationId: id, decision, scores, notes });
}

export async function createManualOpportunity(input: {
  brand: string; market: string; warmSignal: string; aiScore: number;
  expectedInvoice: number; currency: string; publicSummary: string; minimumRole: "connector" | "deal_operator";
}) {
  if (isAdminFixtureMode) {
    const api = await fixtures();
    const state = api.readFixtureState();
    const opportunity: Opportunity = {
      id: crypto.randomUUID(), twentyId: `manual-${crypto.randomUUID()}`, brand: input.brand,
      market: input.market, signal: input.warmSignal, aiScore: input.aiScore,
      expectedInvoice: input.expectedInvoice, currency: input.currency,
      publicSummary: input.publicSummary, status: "open", routeRequired: "closer",
      outcome: "demo_calls", deliverable: "Complete the task brief and attach verifiable proof.",
      bountyMin: 10, bountyMax: 25,
    };
    state.opportunities.unshift(opportunity);
    state.audit.unshift({ id: crypto.randomUUID(), eventType: "opportunity.created_manual", entity: input.brand, createdAt: new Date().toISOString() });
    api.writeFixtureState(state);
    return opportunity.id;
  }
  const result = await invokeFunction<{ id: string }>("create-manual-opportunity", input);
  return result.id;
}

export async function listAdminOpportunities(): Promise<Opportunity[]> {
  if (isAdminFixtureMode) return (await fixtures()).readFixtureState().opportunities;
  const { data, error } = await supabase!.from("opportunities").select("*").order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map((row) => ({
    id: row.id, twentyId: row.twenty_id, brand: row.brand, market: row.market,
    signal: row.warm_signal, aiScore: row.ai_score, expectedInvoice: Number(row.expected_invoice),
    currency: row.currency, publicSummary: row.public_summary, status: row.status,
  }));
}

export async function listServiceHealth(): Promise<ServiceHealth[]> {
  if (isAdminFixtureMode) return [
    { service: "Supabase", status: "not_configured", detail: "Fixture mode is active." },
    { service: "Discord", status: "not_configured", detail: "Waiting for OAuth keys." },
    { service: "Twenty", status: "not_configured", detail: "Waiting for API key and webhook secret." },
    { service: "Brevo", status: "not_configured", detail: "Waiting for sender and API key." },
    { service: "Cloudflare", status: "not_configured", detail: "Waiting for Pages and Turnstile setup." },
  ];
  const { data, error } = await supabase!.from("service_health").select("*").order("service");
  if (error) throw error;
  return data as ServiceHealth[];
}

export async function listCrmOutputCandidates(): Promise<CrmOutputCandidate[]> {
  if (isAdminFixtureMode) return [{
    id: "fixture-crm-candidate",
    assignmentId: "fixture-assignment",
    assignmentTargetId: "fixture-target-runpod",
    workflowId: "fixture-workflow",
    opportunityId: "fixture-opportunity",
    sourceMemberId: "fixture-member",
    productName: "InferenceSaver",
    route: "scout",
    sourceObject: "prospects",
    twentyObject: "prospects",
    candidateType: "qualified_lead",
    targetName: "RunPod AI",
    targetUrl: "https://example.com/runpod",
    workerProof: { icpReason: "AI infrastructure company with visible inference cost pain." },
    reviewDecision: "approved",
    adminCrmDecision: "pending",
    writebackStatus: "pending",
    createdAt: new Date().toISOString(),
  }];
  const { data, error } = await supabase!.from("crm_output_candidates")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(50);
  if (error) throw error;
  return (data ?? []).map(mapCrmOutputCandidate);
}

export async function approveCrmOutputCandidate(candidateId: string) {
  if (isAdminFixtureMode) return;
  const { error } = await supabase!.rpc("admin_approve_crm_output", { candidateId });
  if (error) throw error;
}

export async function rejectCrmOutputCandidate(candidateId: string, notes: string) {
  if (isAdminFixtureMode) return;
  const { error } = await supabase!.rpc("admin_reject_crm_output", { candidateId, notes });
  if (error) throw error;
}

export async function listAudit() {
  if (isAdminFixtureMode) return (await fixtures()).readFixtureState().audit;
  const { data, error } = await supabase!.from("audit_events").select("id,event_type,entity_id,created_at").order("created_at", { ascending: false }).limit(50);
  if (error) throw error;
  return (data ?? []).map((row) => ({ id: String(row.id), eventType: row.event_type, entity: row.entity_id, createdAt: row.created_at }));
}

function mapCrmOutputCandidate(row: Record<string, unknown>): CrmOutputCandidate {
  return {
    id: String(row.id),
    assignmentId: String(row.assignment_id),
    assignmentTargetId: row.assignment_target_id ? String(row.assignment_target_id) : undefined,
    workflowId: String(row.workflow_id),
    opportunityId: String(row.opportunity_id),
    sourceMemberId: String(row.source_member_id),
    productName: String(row.product_name ?? "InferenceSaver"),
    route: row.route as CrmOutputCandidate["route"],
    sourceObject: row.source_object as CrmOutputCandidate["sourceObject"],
    twentyObject: String(row.twenty_object ?? "opportunities"),
    candidateType: String(row.candidate_type ?? "signal"),
    targetName: String(row.target_name ?? "Untitled output"),
    targetUrl: row.target_url ? String(row.target_url) : undefined,
    workerProof: row.worker_proof && typeof row.worker_proof === "object" ? row.worker_proof as Record<string, unknown> : {},
    reviewDecision: String(row.review_decision ?? "approved"),
    adminCrmDecision: row.admin_crm_decision as CrmOutputCandidate["adminCrmDecision"],
    writebackStatus: row.writeback_status as CrmOutputCandidate["writebackStatus"],
    adminNotes: row.admin_notes ? String(row.admin_notes) : undefined,
    createdAt: String(row.created_at),
    decidedAt: row.decided_at ? String(row.decided_at) : undefined,
  };
}

export async function recordInvoice(input: {
  opportunityId: string;
  twentyId: string;
  invoiceReference: string;
  invoiceAmount: number;
  currency: string;
  connectorId: string;
}) {
  if (isAdminFixtureMode) {
    const api = await fixtures();
    const state = api.readFixtureState();
    const opportunity = state.opportunities.find((item) => item.id === input.opportunityId);
    if (!opportunity) throw new Error("Opportunity not found.");
    if (state.ledger.some((entry) => entry.source.startsWith(input.invoiceReference))) throw new Error("Invoice was already recorded.");
    state.ledger.unshift({
      id: crypto.randomUUID(), source: `${input.invoiceReference} / CONNECTOR`,
      amount: Math.round(input.invoiceAmount * 0.05 * 100) / 100, currency: input.currency,
      rate: 0.05, status: "hold", createdAt: new Date().toISOString(),
    });
    opportunity.status = "closed";
    state.audit.unshift({ id: crypto.randomUUID(), eventType: "invoice.cleared", entity: opportunity.brand, createdAt: new Date().toISOString() });
    api.writeFixtureState(state);
    return;
  }
  await invokeFunction("record-invoice-event", {
    ...input,
    operatorId: null,
  });
}

export async function createAndPayPayout(reference: string) {
  if (isAdminFixtureMode) {
    const api = await fixtures();
    const state = api.readFixtureState();
    let paid = 0;
    state.ledger = state.ledger.map((entry) => {
      if (entry.status !== "approved" && entry.status !== "hold") return entry;
      paid += 1;
      return { ...entry, status: "paid", externalReference: reference };
    });
    state.audit.unshift({ id: crypto.randomUUID(), eventType: "payout.batch_paid", entity: `${paid} entries`, createdAt: new Date().toISOString() });
    api.writeFixtureState(state);
    return paid;
  }
  const today = new Date().toISOString().slice(0, 10);
  const start = `${today.slice(0, 8)}01`;
  const { data: batchId, error } = await supabase!.rpc("create_payout_batch", { p_period_start: start, p_period_end: today });
  if (error) throw error;
  const result = await invokeFunction<{ paidEntries: number }>("mark-payout-paid", { batchId, externalReference: reference });
  return result.paidEntries;
}
